import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Download,
  Search,
  Filter,
  Eye,
  Send,
  Lock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileText,
  Users,
  Calendar,
  Wallet
} from 'lucide-react';
import { formatCurrency, formatDate } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const TokenholderRegistry = () => {
  const navigate = useNavigate();
  const { logout, getDisplayName } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [bondFilter, setBondFilter] = useState('all');
  const [selectedTokens, setSelectedTokens] = useState([]);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferDetails, setTransferDetails] = useState({
    fromWallet: '',
    toWallet: '',
    amount: 0
  });

  // Mock tokenholder data
  const tokenholders = [
    {
      id: 1,
      investorId: 'INV001',
      investorName: 'Pension Fund Malaysia',
      bondName: 'Green Infrastructure Sukuk 2024',
      bondId: 'BOND001',
      tokenQuantity: 166666667,
      walletAddress: '0x1234567890abcdef1234567890abcdef12345678',
      tokenStatus: 'Active',
      lastTransferDate: '2024-12-20',
      maturityDate: '2029-12-20',
      couponRate: 5.25,
      isMatured: false,
      transferHistory: 3
    },
    {
      id: 2,
      investorId: 'INV002',
      investorName: 'Insurance Corp Bhd',
      bondName: 'Green Infrastructure Sukuk 2024',
      bondId: 'BOND001',
      tokenQuantity: 250000000,
      walletAddress: '0xabcdef1234567890abcdef1234567890abcdef12',
      tokenStatus: 'Active',
      lastTransferDate: '2024-12-20',
      maturityDate: '2029-12-20',
      couponRate: 5.25,
      isMatured: false,
      transferHistory: 1
    },
    {
      id: 3,
      investorId: 'INV003',
      investorName: 'Investment Bank Ltd',
      bondName: 'Green Infrastructure Sukuk 2024',
      bondId: 'BOND001',
      tokenQuantity: 83333333,
      walletAddress: '0x567890abcdef1234567890abcdef1234567890ab',
      tokenStatus: 'Active',
      lastTransferDate: '2024-12-20',
      maturityDate: '2029-12-20',
      couponRate: 5.25,
      isMatured: false,
      transferHistory: 2
    },
    {
      id: 4,
      investorId: 'INV004',
      investorName: 'Sovereign Wealth Fund',
      bondName: 'Renewable Energy Sukuk',
      bondId: 'BOND002',
      tokenQuantity: 120000000,
      walletAddress: '0x9876543210fedcba9876543210fedcba98765432',
      tokenStatus: 'Active',
      lastTransferDate: '2024-12-18',
      maturityDate: '2028-12-25',
      profitRate: 5.50,
      isMatured: false,
      transferHistory: 1
    },
    {
      id: 5,
      investorId: 'INV005',
      investorName: 'Takaful Malaysia',
      bondName: 'Legacy Corporate Bond',
      bondId: 'BOND003',
      tokenQuantity: 50000000,
      walletAddress: '0xfedcba9876543210fedcba9876543210fedcba98',
      tokenStatus: 'Matured',
      lastTransferDate: '2023-10-15',
      maturityDate: '2024-10-15',
      couponRate: 4.75,
      isMatured: true,
      transferHistory: 5
    },
    {
      id: 6,
      investorId: 'INV006',
      investorName: 'Real Estate Investment Trust',
      bondName: 'Tech Development Bond 2024',
      bondId: 'BOND004',
      tokenQuantity: 75000000,
      walletAddress: '0x456789abc123def456789abc123def456789abc1',
      tokenStatus: 'Suspended',
      lastTransferDate: '2024-12-10',
      maturityDate: '2026-12-22',
      couponRate: 6.00,
      isMatured: false,
      transferHistory: 0
    }
  ];

  const filteredTokenholders = tokenholders.filter(holder => {
    const matchesSearch =
      holder.investorId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      holder.investorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      holder.bondName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      holder.walletAddress.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || holder.tokenStatus.toLowerCase() === statusFilter;
    const matchesBond = bondFilter === 'all' || holder.bondId === bondFilter;

    return matchesSearch && matchesStatus && matchesBond;
  });

  const getStatusBadge = (status, isMatured) => {
    if (isMatured) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <Lock className="w-3 h-3 mr-1" />
          Matured
        </span>
      );
    }

    const statusConfig = {
      'Active': { color: 'green', icon: CheckCircle },
      'Suspended': { color: 'yellow', icon: AlertTriangle },
      'Transferred': { color: 'blue', icon: Send }
    };

    const config = statusConfig[status] || { color: 'gray', icon: CheckCircle };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  const exportToCSV = () => {
    const headers = [
      'Investor ID',
      'Investor Name',
      'Bond Name',
      'Token Quantity',
      'Wallet Address',
      'Token Status',
      'Last Transfer Date',
      'Maturity Date',
      'Rate (%)',
      'Transfer History'
    ];

    const rows = filteredTokenholders.map(holder => [
      holder.investorId,
      holder.investorName,
      holder.bondName,
      holder.tokenQuantity,
      holder.walletAddress,
      holder.tokenStatus,
      formatDate(holder.lastTransferDate),
      formatDate(holder.maturityDate),
      holder.couponRate || holder.profitRate,
      holder.transferHistory
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', `tokenholder_registry_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const exportToPDF = () => {
    alert('PDF export functionality - This would generate a PDF report with all tokenholder data formatted for reconciliation with RENTAS/internal ledger systems.');
  };

  const handleTransfer = (tokenholder) => {
    if (tokenholder.isMatured) {
      alert('Transfer blocked: This token is no longer transferable due to maturity.');
      return;
    }

    setTransferDetails({
      fromWallet: tokenholder.walletAddress,
      toWallet: '',
      amount: 0,
      selectedTokenholder: tokenholder
    });
    setShowTransferModal(true);
  };

  const confirmTransfer = () => {
    if (transferDetails.selectedTokenholder.isMatured) {
      alert('Transfer blocked: This token is no longer transferable due to maturity.');
      return;
    }

    console.log('Initiating transfer:', transferDetails);
    alert(`Transfer initiated:\nFrom: ${transferDetails.fromWallet}\nTo: ${transferDetails.toWallet}\nAmount: ${formatCurrency(transferDetails.amount)}`);
    setShowTransferModal(false);
    setTransferDetails({
      fromWallet: '',
      toWallet: '',
      amount: 0
    });
  };

  const uniqueBonds = [...new Set(tokenholders.map(h => h.bondId))];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Tokenholder Registry</h1>
              <p className="mt-2 text-sm text-gray-600">Live data registry for all issued bond tokens</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Tokenholders
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {tokenholders.length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Active Tokens
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {tokenholders.filter(h => h.tokenStatus === 'Active').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Lock className="h-6 w-6 text-gray-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Matured Tokens
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {tokenholders.filter(h => h.isMatured).length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Wallet className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Token Value
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {formatCurrency(tokenholders.reduce((sum, h) => sum + h.tokenQuantity, 0))}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search investors, bonds, or wallets..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="transferred">Transferred</option>
            </select>

            <select
              value={bondFilter}
              onChange={(e) => setBondFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Bonds</option>
              {uniqueBonds.map(bondId => (
                <option key={bondId} value={bondId}>{bondId}</option>
              ))}
            </select>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={exportToCSV}
              className="flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </button>
            <button
              onClick={exportToPDF}
              className="flex items-center px-4 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-colors"
            >
              <FileText className="h-4 w-4 mr-2" />
              Export PDF
            </button>
          </div>
        </div>

        {/* Tokenholder Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Investor Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bond Information
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Token Quantity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Wallet Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Token Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Transfer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTokenholders.map((holder) => (
                  <tr key={holder.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {holder.investorId}
                        </div>
                        <div className="text-sm text-gray-500">
                          {holder.investorName}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {holder.bondName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {holder.couponRate ? `${holder.couponRate}% Coupon` : `${holder.profitRate}% Profit`} •
                          Maturity: {formatDate(holder.maturityDate)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatCurrency(holder.tokenQuantity)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 font-mono">
                        <div className="truncate max-w-32" title={holder.walletAddress}>
                          {holder.walletAddress.slice(0, 10)}...{holder.walletAddress.slice(-8)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(holder.tokenStatus, holder.isMatured)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(holder.lastTransferDate)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {holder.transferHistory} transfers
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          className="text-blue-600 hover:text-blue-900 flex items-center"
                          onClick={() => alert(`Investor Details:\n${JSON.stringify(holder, null, 2)}`)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </button>
                        <button
                          onClick={() => handleTransfer(holder)}
                          className={`flex items-center ${holder.isMatured
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-green-600 hover:text-green-900'
                            }`}
                          disabled={holder.isMatured}
                        >
                          <Send className="h-4 w-4 mr-1" />
                          Transfer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {filteredTokenholders.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No tokenholders found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No tokenholders match the current filter criteria.
            </p>
          </div>
        )}
      </div>

      {/* Transfer Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center mb-4">
              <Send className="h-6 w-6 text-green-600 mr-3" />
              <h3 className="text-lg font-medium text-gray-900">
                Transfer Tokens
              </h3>
            </div>
            <div className="mb-6">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-gray-900">
                  {transferDetails.selectedTokenholder?.investorName}
                </h4>
                <p className="text-sm text-gray-600">
                  Current Balance: {formatCurrency(transferDetails.selectedTokenholder?.tokenQuantity)}
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From Wallet
                  </label>
                  <input
                    type="text"
                    value={transferDetails.fromWallet}
                    readOnly
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-50 font-mono"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To Wallet Address
                  </label>
                  <input
                    type="text"
                    value={transferDetails.toWallet}
                    onChange={(e) => setTransferDetails({ ...transferDetails, toWallet: e.target.value })}
                    placeholder="0x..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transfer Amount
                  </label>
                  <input
                    type="number"
                    value={transferDetails.amount}
                    onChange={(e) => setTransferDetails({ ...transferDetails, amount: Number(e.target.value) })}
                    placeholder="0"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowTransferModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmTransfer}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"
              >
                Confirm Transfer
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TokenholderRegistry; 